import { useEffect, useState, useCallback } from "react";
import {
  Drawer,
  Descriptions,
  Tag,
  Button,
  Space,
  Spin,
  Alert,
  Tooltip,
  Typography,
  Switch,
} from "antd";
import {
  EditOutlined,
  SaveOutlined,
  ReloadOutlined,
  StarFilled,
  StarOutlined,
} from "@ant-design/icons";
import TagSelector from "../../common/TagSelector";
import apiHelpers from "./data";
import { STORES } from "../BlogsTable/stores";
import { triggerNotification } from "../../common/notify";

const labelForHash = (hash) =>
  STORES.find((s) => s.hash === hash)?.label || "";

const fmt = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleString("en-GB");
};

// ── Video Tags and Points sub-drawer (TagSelector-based) ──────────────────────

const tagsKey = (tags) => tags.map((t) => `${t.id}:${t.points}`).join(",");

const KpDrawer = ({ open, video, onClose, onChanged, onCountChange }) => {
  const videoId = video?.id ?? null;

  const [selectedTags, setSelectedTags] = useState([]);
  const [savedKey,     setSavedKey]     = useState("");
  const [loading, setLoading]           = useState(false);
  const [saving, setSaving]             = useState(false);
  const [isDefault, setIsDefault]       = useState(false);
  const [togglingDefault, setTogglingDefault] = useState(false);

  const isDirty = tagsKey(selectedTags) !== savedKey;

  useEffect(() => {
    setIsDefault(Number(video?.isDefault ?? video?.is_default) === 1);
  }, [video]);

  useEffect(() => {
    if (videoId != null) onCountChange?.(String(videoId), selectedTags.length);
  }, [videoId, selectedTags.length, onCountChange]);

  const handleToggleDefault = async (checked) => {
    if (!videoId) return;
    setTogglingDefault(true);
    try {
      await apiHelpers.updateVideo(videoId, { is_default: checked ? 1 : 0 });
      setIsDefault(checked);
      triggerNotification({
        type: "success",
        title: checked ? "Default set" : "Default removed",
        message: `"${video?.title || video?.bamvideoId}" ${checked ? "is now" : "is no longer"} the default.`,
      });
      onChanged?.();
    } catch (err) {
      const mapped = apiHelpers.mapError(err);
      triggerNotification({ type: "error", title: "Failed to set default", message: mapped.message });
    } finally {
      setTogglingDefault(false);
    }
  };

  const fetchRows = useCallback(async () => {
    if (!videoId) return;
    setLoading(true);
    try {
      const data = await apiHelpers.fetchVideoById(videoId);
      const kps = Array.isArray(data?.keyword_points) ? data.keyword_points : [];
      const tags = kps.map((r) => ({
        id: r.tagId ?? r.id,
        tag: r.keyword,
        points: Number(r.points) || 0,
      }));
      setSelectedTags(tags);
      setSavedKey(tagsKey(tags));
    } catch (err) {
      const mapped = apiHelpers.mapError(err);
      triggerNotification({ type: "error", title: "Failed to load keywords", message: mapped.message });
    } finally {
      setLoading(false);
    }
  }, [videoId]);

  useEffect(() => {
    if (open) fetchRows();
    else { setSelectedTags([]); setSavedKey(""); }
  }, [open, fetchRows]);

  const handleSave = async () => {
    if (saving) return;
    setSaving(true);
    try {
      const items = selectedTags.map((t) => ({
        tag_id: Number(t.id),
        points: Number(t.points) || 0,
      }));
      const result = await apiHelpers.bulkUpsertKp(videoId, items);
      const kps = result?.items || [];
      const savedTags = kps.map((r) => ({
        id: r.tagId ?? r.id,
        tag: r.keyword,
        points: Number(r.points) || 0,
      }));
      setSelectedTags(savedTags);
      setSavedKey(tagsKey(savedTags));
      triggerNotification({
        type: "success",
        title: `${items.length} tag${items.length === 1 ? "" : "s"} saved`,
      });
      onChanged?.();
    } catch (err) {
      const mapped = apiHelpers.mapError(err);
      triggerNotification({ type: "error", title: "Save failed", message: mapped.message });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Drawer
      open={open}
      onClose={onClose}
      maskClosable={false}
      width={600}
      title="Video Tags and Points"
      destroyOnClose
      extra={
        <Space>
          <Tooltip title="Refresh">
            <Button icon={<ReloadOutlined />} onClick={fetchRows} loading={loading} disabled={saving} />
          </Tooltip>
          <Button
            type="primary"
            icon={<SaveOutlined />}
            loading={saving}
            onClick={handleSave}
            disabled={loading || !isDirty}
          >
            Save tags
          </Button>
        </Space>
      }
    >
      {video && (
        <div style={{ marginBottom: 16 }}>
          <Descriptions size="small" column={1} bordered style={{ marginBottom: 12 }}
            items={[
              { key: "title", label: "Title", children: video.title || <Typography.Text type="secondary">—</Typography.Text> },
              { key: "bamvideo", label: "Bambuser Video ID", children: <Typography.Text code>{video.bamvideoId}</Typography.Text> },
            ]}
          />
          <Space>
            <Switch
              checked={isDefault}
              checkedChildren={<StarFilled />}
              loading={togglingDefault}
              onChange={handleToggleDefault}
            />
            <Typography.Text>Set as default video</Typography.Text>
          </Space>
        </div>
      )}

      {loading ? (
        <div style={{ textAlign: "center", padding: 40 }}>
          <Spin />
        </div>
      ) : (
        <>
          <Typography.Text type="secondary" style={{ fontSize: 12, display: "block", marginBottom: 8 }}>
            Select tags from the global tags library. Drag to reorder. Set points per tag.
          </Typography.Text>
          <TagSelector
            value={selectedTags}
            onChange={setSelectedTags}
            withPoints
            placeholder="Search and select tags…"
          />
          <div style={{ marginTop: 16 }}>
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              {selectedTags.length} tag{selectedTags.length === 1 ? "" : "s"} selected
            </Typography.Text>
          </div>
        </>
      )}
    </Drawer>
  );
};

// ── Main VideoDetailDrawer ────────────────────────────────────────────────────

const VideoDetailDrawer = ({ open, videoId, onClose, onEdit }) => {
  const [video, setVideo]       = useState(null);
  const [loading, setLoading]   = useState(false);
  const [loadError, setLoadError] = useState(null);

  const reset = useCallback(() => {
    setVideo(null);
    setLoading(false);
    setLoadError(null);
  }, []);

  const fetchData = useCallback(async () => {
    if (!videoId) return;
    setLoading(true);
    setLoadError(null);
    try {
      const data = await apiHelpers.fetchVideoById(videoId);
      setVideo(data);
    } catch (err) {
      const mapped = apiHelpers.mapError(err);
      setLoadError(mapped);
      if (mapped.code !== "NOT_FOUND") {
        triggerNotification({ type: "error", title: "Failed to load video", message: mapped.message });
      }
    } finally {
      setLoading(false);
    }
  }, [videoId]);

  useEffect(() => {
    if (!open) { reset(); return; }
    fetchData();
  }, [open, fetchData, reset]);

  const isActive  = true;
  const isDefault = Number(video?.isDefault ?? video?.is_default) === 1;
  const storeLbl  = video?.storeLabel || video?.store_label || labelForHash(video?.storeHash || video?.store_hash || "");

  return (
    <Drawer
        open={open}
        onClose={onClose}
        maskClosable={false}
        width={780}
        title={
          video ? (
            <Space size={12}>
              <span>{video.title || video.bamvideoId}</span>
              {isDefault && <Tag color="gold" icon={<StarFilled />}>Default</Tag>}
              {!isActive && <Tag>Inactive</Tag>}
            </Space>
          ) : "Video detail"
        }
        destroyOnClose
        extra={
          video && (
            <Space>
              <Tooltip title="Refresh"><Button icon={<ReloadOutlined />} onClick={fetchData} disabled={loading} /></Tooltip>
              <Button icon={<EditOutlined />} onClick={() => onEdit?.(video.id)}>Edit</Button>
            </Space>
          )
        }
      >
        {loading && !video ? (
          <div style={{ textAlign: "center", padding: 40 }}><Spin /></div>
        ) : loadError && loadError.code === "NOT_FOUND" ? (
          <Alert type="warning" showIcon message="Video not found" description="This video may have been deleted. Close the drawer and refresh the list." />
        ) : video ? (
          <>
            <Descriptions size="small" column={2} bordered
              items={[
                { key: "id", label: "ID", children: <Typography.Text code>{String(video.id)}</Typography.Text> },
                { key: "bamvideo", label: "Bambuser Video ID", children: <Typography.Text code>{video.bamvideoId}</Typography.Text> },
                { key: "store", label: "Store", children: storeLbl ? <Tag color="geekblue">{storeLbl}</Tag> : <Typography.Text type="secondary">—</Typography.Text> },
                { key: "storehash", label: "Store hash", children: video.storeHash ? <Typography.Text code>{video.storeHash}</Typography.Text> : <Typography.Text type="secondary">—</Typography.Text> },
                { key: "title", label: "Title", span: 2, children: video.title || <Typography.Text type="secondary">—</Typography.Text> },
                { key: "desc", label: "Description", span: 2, children: video.description || <Typography.Text type="secondary">—</Typography.Text> },
                { key: "default", label: "Default", children: isDefault ? <Tag color="gold" icon={<StarFilled />}>Yes</Tag> : <Tag icon={<StarOutlined />}>No</Tag> },
                { key: "created", label: "Created", children: fmt(video.createdAt) },
                { key: "updated", label: "Updated", children: fmt(video.updatedAt) },
              ]}
            />

          </>
        ) : (
          <div style={{ textAlign: "center", padding: 40 }}><Spin /></div>
        )}
    </Drawer>
  );
};

export { KpDrawer };
export default VideoDetailDrawer;
